import { useMemo } from 'react'

import { getActiveChainId } from '@/config/active'
import type { PoolIdentity } from '@/data/marketApi/types'
import { usePoolsIdentity } from '@/data/marketApi/hooks/usePoolsIdentity'
import type { Currency } from '@/sdk'

import { useNutrimaticMarketOptional } from './NutrimaticMarketProvider'
import { nutrimaticPoolRowToSummary } from './nutrimaticPoolSummary'
import { usePairAddressForCurrencies } from './usePairMarketData'

/**
 * Pool identity for market presentation (logos, pair label, base/quote).
 * Prefer Nutrimatic hub listing — product-config seed is fallback for ops-only pairs.
 */
export function useIndexedPoolForCurrencies(
  input?: Currency | null,
  output?: Currency | null,
): PoolIdentity | undefined {
  const chainId = getActiveChainId()
  void chainId
  const pairAddress = usePairAddressForCurrencies(input, output)
  const hub = useNutrimaticMarketOptional()
  const { data: seedPools } = usePoolsIdentity()

  return useMemo(() => {
    const key = pairAddress?.toLowerCase()
    if (!key) return undefined

    if (hub?.poolRows.length) {
      const row = hub.poolRows.find((r) => r.poolAddress === key)
      if (row) {
        const summary = nutrimaticPoolRowToSummary(row)
        return {
          id: summary.id,
          pair: summary.pair,
          pairAddress: summary.pairAddress,
          token0: summary.token0,
          token1: summary.token1,
          baseToken: summary.baseToken,
          quoteToken: summary.quoteToken,
        } satisfies PoolIdentity
      }
    }

    return seedPools?.find((pool) => pool.pairAddress.toLowerCase() === key)
  }, [pairAddress, hub?.poolRows, seedPools])
}
